import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Sprint 63.6 — Slate Bleed Guard. Ownership is a per-slate number: when the
// active slate changes and competitor sources were entered for a different
// one, offer to clear them so showdown/main ownership never mixes.
const EVT = "wp:field-model-bleed";

export const announceSlateApply = (result) => {
  const fm = result?.field_model;
  if (!fm) return;
  if (fm.bleed) { window.dispatchEvent(new CustomEvent(EVT, { detail: fm })); return; }
  // Sprint 63.7 — per-slate memory: sources swap with the slate instead of bleeding.
  if (fm.changed && (fm.restored?.length || fm.stashed?.length)) {
    const r = fm.restored?.length || 0;
    const s = fm.stashed?.length || 0;
    toast.info(r
      ? `Field Model: restored ${r} source${r === 1 ? "" : "s"} entered for this slate${s ? ` · ${s} parked for slate ${fm.from_dg}` : ""}`
      : `Field Model: no sources entered for this slate yet · ${s} parked for slate ${fm.from_dg} (they return when you switch back)`,
      { duration: 6000 });
    window.dispatchEvent(new CustomEvent("wp:field-model-changed"));
  }
};

export default function SlateBleedGuard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const on = (e) => setInfo(e.detail);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  if (!info) return null;
  const clear = async () => {
    setBusy(true);
    try {
      const r = await http.post("/competitors/clear-all");
      toast.success(`Cleared ${r.data.cleared} competitor source${r.data.cleared === 1 ? "" : "s"} — enter this slate's numbers in Field Model`);
      window.dispatchEvent(new CustomEvent("wp:field-model-changed"));
      setInfo(null);
    } catch (e) { toast.error("Clear failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="slate-bleed-dialog">
      <div className="w-full max-w-md bg-[#121212] border border-[#FFCC00]/60 p-5" style={{ boxShadow: "0 0 30px rgba(255,204,0,0.2)" }}>
        <div className="flex items-center gap-2 text-[#FFCC00] mono text-[10px] uppercase tracking-widest mb-2">
          <Warning size={14} weight="fill" /> Slate changed · Field Model
        </div>
        <h3 className="heading text-white font-black text-xl leading-tight mb-2">
          Your {info.sources.length} competitor source{info.sources.length === 1 ? " was" : "s were"} entered for a different slate
        </h3>
        <p className="text-[12px] text-zinc-400 leading-relaxed mb-4">
          Ownership is a per-slate number — a player who is 8% chalk on the main slate can be 45% on a two-team showdown.
          Keeping <span className="text-white">{info.sources.join(", ")}</span> (slate {info.from_dg}) while building slate {info.to_dg} would
          feed the wrong ownership into the shadow field, the copies estimate and the bootstrap.
        </p>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => setInfo(null)} data-testid="slate-bleed-keep"
            className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 px-3 py-2">
            Keep anyway
          </button>
          <button type="button" onClick={clear} disabled={busy} data-testid="slate-bleed-clear"
            className="mono text-[10px] uppercase tracking-widest bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-50 text-black font-black px-3 py-2">
            {busy ? "Clearing…" : "Clear sources for this slate"}
          </button>
        </div>
      </div>
    </div>
  );
}
